import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Save, LogOut, Trash2, Plus, AlertTriangle, Printer, Pencil, CalendarCheck, CalendarPlus } from 'lucide-react';
import {
  getDischarge, saveDischarge, reopenDischarge,
  type DischargeMedicine, type DischargePayload,
} from '@/lib/ipdApi';
import DrugSearchInput from '@/components/shared/DrugSearchInput';
import type { DrugSearchResult } from '@/lib/api';
import UnifiedPrintModal from '@/components/Print/UnifiedPrintModal';
import { format } from 'date-fns';
import { usePermission } from '@/hooks/usePermission';
import { getAppointmentsByDateRange } from '@/lib/api';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const CONDITIONS  = ['improved', 'stable', 'same', 'deteriorated', 'expired'] as const;
// When: food and time of day, as on the prescription pad — how often is the Frequency column
const TAKEN_WHEN  = ['After food', 'Before food', 'With food', 'Empty stomach', 'Morning', 'Night', 'Bedtime'] as const;
const FREQS       = ['OD', 'BD', 'TDS', 'QID', 'Q6H', 'SOS', 'Weekly'] as const;
const DRUG_TYPES  = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Drops', 'Cream', 'Inhaler', 'Other'] as const;

/** The Type column for a catalog drug's form (tablet / capsule / syrup / suspension / …) */
function typeFor(form: string | null | undefined): string {
  const f = String(form ?? '').toLowerCase();
  if (f === 'suspension') return 'Syrup';
  return DRUG_TYPES.find(t => t.toLowerCase() === f) ?? 'Other';
}

// ─── Form state ───────────────────────────────────────────────────────────────

interface FormState {
  final_diagnosis:       string;
  complaints:            string;
  patient_history:       string;
  key_findings_physical: string;
  investigation_summary: string;
  procedures_done:       string;
  operative_findings:    string;
  hospital_course:       string;
  treatment_given:       string;
  condition_at_discharge: string;
  discharge_advice:      string;
  additional_medication: string;
  others:                string;
  tests_requested:       string;
  follow_up_date:        string;
  follow_up_notes:       string;
  diet_advice:           string;
  activity_restriction:  string;
  acknowledgement:       string;
  medicines:             DischargeMedicine[];
}

const emptyForm = (): FormState => ({
  final_diagnosis: '', complaints: '', patient_history: '',
  key_findings_physical: '', investigation_summary: '',
  procedures_done: '', operative_findings: '', hospital_course: '',
  treatment_given: '', condition_at_discharge: 'improved',
  discharge_advice: '', additional_medication: '', others: '',
  tests_requested: '', follow_up_date: '', follow_up_notes: '',
  diet_advice: '', activity_restriction: '', acknowledgement: '',
  medicines: [],
});

// ─── Shared small components ──────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[15px] font-semibold text-foreground mb-3 border-b border-border pb-1.5">
      {children}
    </h3>
  );
}

function TextArea({
  label, value, onChange, rows = 3, placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void;
  rows?: number; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-label mb-1">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={cn(
          'w-full rounded-md border border-field bg-card px-3 py-2',
          'text-sm resize-y focus:outline-none focus:ring-1 focus:ring-ring',
          'placeholder:text-muted-foreground',
        )}
      />
    </div>
  );
}

function LabeledInput({
  label, value, onChange, type = 'text', placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-label mb-1">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-sm bg-card"
      />
    </div>
  );
}

function Select({
  label, value, onChange, options,
}: {
  label: string; value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[] | readonly string[];
}) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-label mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-8 w-full rounded-md border border-field bg-card px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {(options as any[]).map((o: any) =>
          typeof o === 'string'
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>
        )}
      </select>
    </div>
  );
}

// ─── Medicine row ─────────────────────────────────────────────────────────────

function MedicineRow({
  med, index, onChange, onDelete,
}: {
  med: DischargeMedicine;
  index: number;
  onChange: (m: DischargeMedicine) => void;
  onDelete: () => void;
}) {
  const set = (k: keyof DischargeMedicine) => (v: any) => onChange({ ...med, [k]: v });

  const handleDrugSelect = (drug: DrugSearchResult) => {
    onChange({ ...med, drug_id: drug.id, drug_name: drug.brand_name, drug_type: med.drug_type || typeFor(drug.form) });
  };

  return (
    <tr className="border-b last:border-0 group">
      <td className="px-2 py-1.5 text-xs text-muted-foreground text-center w-8">{index + 1}</td>

      {/* Type */}
      <td className="px-1 py-1.5 w-28">
        <select
          value={med.drug_type || ''}
          onChange={e => set('drug_type')(e.target.value)}
          className="w-full h-7 rounded border border-field bg-background px-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">—</option>
          {DRUG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>

      {/* Medicine name */}
      <td className="px-1 py-1.5 min-w-[160px]">
        <div className="border rounded px-2 py-1 bg-background focus-within:ring-1 focus-within:ring-ring">
          <DrugSearchInput
            value={med.drug_name}
            onChange={handleDrugSelect}
            onCustomInput={v => onChange({ ...med, drug_id: null, drug_name: v })}
            placeholder="Medicine…"
          />
        </div>
      </td>

      {/* Dose */}
      <td className="px-1 py-1.5 w-24">
        <Input value={med.dose || ''} onChange={e => set('dose')(e.target.value)}
          placeholder="500mg" className="h-7 text-xs" />
      </td>

      {/* When */}
      <td className="px-1 py-1.5 w-32">
        <select value={med.taken_when || ''} onChange={e => set('taken_when')(e.target.value)}
          className="w-full h-7 rounded border border-field bg-background px-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring">
          <option value="">—</option>
          {TAKEN_WHEN.map(t => <option key={t} value={t}>{t}</option>)}
          {/* A value saved before this list (e.g. "TDS") stays shown */}
          {med.taken_when && !(TAKEN_WHEN as readonly string[]).includes(med.taken_when) && (
            <option value={med.taken_when}>{med.taken_when}</option>
          )}
        </select>
      </td>

      {/* Frequency */}
      <td className="px-1 py-1.5 w-20">
        <select value={med.frequency || ''} onChange={e => set('frequency')(e.target.value)}
          className="w-full h-7 rounded border border-field bg-background px-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring">
          <option value="">—</option>
          {FREQS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </td>

      {/* Duration */}
      <td className="px-1 py-1.5 w-24">
        <Input value={med.duration || ''} onChange={e => set('duration')(e.target.value)}
          placeholder="5 days" className="h-7 text-xs" />
      </td>

      {/* Notes */}
      <td className="px-1 py-1.5 min-w-[100px]">
        <Input value={med.notes || ''} onChange={e => set('notes')(e.target.value)}
          placeholder="Optional" className="h-7 text-xs" />
      </td>

      {/* Delete */}
      <td className="px-1 py-1.5 w-8">
        <button
          onClick={onDelete}
          className="[@media(hover:hover)]:opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  admissionId: string; admissionInfo?: any; onFinalized?: () => void;
  /** ipd:write — without it the summary can be read and printed, not saved */
  canWrite: boolean;
}

export default function IPDDischargeSummaryTab({ admissionId, admissionInfo, onFinalized, canWrite }: Props) {
  const [form,        setForm]        = useState<FormState>(emptyForm());
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [savedMsg,    setSavedMsg]    = useState('');
  const [admission,   setAdmission]   = useState<any>(null);
  const [summary,     setSummary]     = useState<any>(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [printOpen,    setPrintOpen]    = useState(false);
  const [amendOpen,    setAmendOpen]    = useState(false);
  const [amendReason,  setAmendReason]  = useState('');
  const [amendError,   setAmendError]   = useState('');

  const isFinalized = !!summary?.finalized_at;
  // Signing off (finalize, amend) is a doctor's act; others can prepare the draft
  const { can } = usePermission();
  const canSign = can('ipd:clinical');

  const handleAmend = async () => {
    setAmendError('');
    if (!amendReason.trim()) { setAmendError('Say what needs correcting.'); return; }
    try {
      setSaving(true);
      await reopenDischarge(admissionId, amendReason.trim());
      setAmendOpen(false);
      await load();
    } catch (e: any) {
      setAmendError(e.message || 'Failed to reopen the summary.');
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDischarge(admissionId);
      if (!data) return;
      setAdmission(data.admission);
      setSummary(data.summary);

      if (data.summary) {
        const s = data.summary;
        setForm({
          final_diagnosis:       s.final_diagnosis        || '',
          complaints:            s.complaints             || '',
          patient_history:       data.admission.patient_history || '',
          key_findings_physical: s.key_findings_physical  || '',
          investigation_summary: s.investigation_summary  || '',
          procedures_done:       s.procedures_done        || '',
          operative_findings:    s.operative_findings     || '',
          hospital_course:       s.hospital_course        || '',
          treatment_given:       s.treatment_given        || '',
          condition_at_discharge: s.condition_at_discharge || 'improved',
          discharge_advice:      s.discharge_advice       || '',
          additional_medication: s.additional_medication  || '',
          others:                s.others                 || '',
          tests_requested:       s.tests_requested        || '',
          follow_up_date:        s.follow_up_date         || '',
          follow_up_notes:       s.follow_up_notes        || '',
          diet_advice:           s.diet_advice            || '',
          activity_restriction:  s.activity_restriction   || '',
          acknowledgement:       s.acknowledgement        || '',
          medicines:             data.medicines,
        });
      } else {
        // First open — pre-populate from the stay. Medicines: the active orders the patient can take home
        // (an IV / IM / SC order stays on the ward); treatment given: every order; diagnosis: the provisional one
        const injected = (o: { route?: string | null }) => ['iv', 'im', 'sc'].includes(String(o.route ?? '').toLowerCase());
        const treatment = data.activeOrders
          .map(o => [o.drug_name, o.dose, String(o.route ?? '').toUpperCase(), o.frequency].filter(Boolean).join(' '))
          .join('\n');
        const prePopMeds: DischargeMedicine[] = data.activeOrders.filter(o => !injected(o)).map(o => ({
          drug_id:   o.drug_id ?? null,
          drug_name: o.drug_name,
          drug_type: '',
          dose:      o.dose,
          taken_when: '',
          frequency: o.frequency,
          duration:  '',
          notes:     o.special_instructions || '',
        }));
        setForm(f => ({
          ...f,
          final_diagnosis: data.admission.admission_reason || '',
          treatment_given: treatment,
          patient_history: data.admission.patient_history || '',
          medicines: prePopMeds,
        }));
      }
    } catch (e) {
      console.error('Failed to load discharge:', e);
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const set = <K extends keyof FormState>(k: K) => (v: FormState[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const addMedicine = () =>
    setForm(f => ({
      ...f,
      medicines: [...f.medicines, { drug_name: '', drug_id: null }],
    }));

  const updateMed = (i: number, m: DischargeMedicine) =>
    setForm(f => {
      const meds = [...f.medicines];
      meds[i] = m;
      return { ...f, medicines: meds };
    });

  const deleteMed = (i: number) =>
    setForm(f => ({
      ...f,
      medicines: f.medicines.filter((_, idx) => idx !== i),
    }));

  const buildPayload = (finalize: boolean): DischargePayload => ({
    final_diagnosis:       form.final_diagnosis,
    complaints:            form.complaints       || null as any,
    patient_history:       form.patient_history,
    key_findings_physical: form.key_findings_physical || null as any,
    investigation_summary: form.investigation_summary || null as any,
    procedures_done:       form.procedures_done       || null as any,
    operative_findings:    form.operative_findings    || null as any,
    hospital_course:       form.hospital_course       || null as any,
    treatment_given:       form.treatment_given       || null as any,
    condition_at_discharge: form.condition_at_discharge as any,
    discharge_advice:      form.discharge_advice      || null as any,
    additional_medication: form.additional_medication || null as any,
    others:                form.others                || null as any,
    tests_requested:       form.tests_requested       || null as any,
    follow_up_date:        form.follow_up_date        || null as any,
    follow_up_notes:       form.follow_up_notes       || null as any,
    diet_advice:           form.diet_advice           || null as any,
    activity_restriction:  form.activity_restriction  || null as any,
    acknowledgement:       form.acknowledgement       || null as any,
    medicines: form.medicines.filter(m => m.drug_name?.trim()),
    finalize,
  });

  const handleSaveDraft = async () => {
    setError(''); setSavedMsg('');
    try {
      setSaving(true);
      await saveDischarge(admissionId, buildPayload(false));
      setSavedMsg('Draft saved.');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    setError(''); setSavedMsg('');
    if (form.condition_at_discharge !== 'expired' && !form.final_diagnosis.trim()) {
      setError('Final Diagnosis is required before finalizing.');
      setFinalizeOpen(false);
      return;
    }
    try {
      setSaving(true);
      await saveDischarge(admissionId, buildPayload(true));
      setFinalizeOpen(false);
      await load(); // refresh to show finalized state
      onFinalized?.();
    } catch (e: any) {
      setError(e.message || 'Failed to finalize discharge.');
    } finally {
      setSaving(false);
    }
  };

  const buildPrintData = () => ({
    doctor_id:  admission?.admitting_doctor_id,
    clinic_id:  admission?.clinic_id,
    patient_id: admission?.patient_id,
    admission_number:        admission?.admission_number,
    admission_date:          admission?.admission_date,
    discharge_date:          admission?.discharge_date,
    status:                  admission?.status,
    admitting_doctor_name:   admission?.admitting_doctor_name,
    patient_name:            admissionInfo?.patient_name,
    patient_mrn:             admissionInfo?.patient_mrn,
    patient_age:             admissionInfo?.patient_age,
    patient_gender:          admissionInfo?.patient_gender,
    ward_name:               admissionInfo?.ward_name,
    bed_number:              admissionInfo?.bed_number,
    consulting_doctor_name:  admissionInfo?.consulting_doctor_name,
    ...form,
  });

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-6">

      {/* Print Summary */}
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={() => setPrintOpen(true)}>
          <Printer className="w-4 h-4 mr-1.5" /> Print summary
        </Button>
      </div>

      {/* Admission status banner */}
      {admission?.status !== 'admitted' && (
        <div className="rounded-md bg-success/10 border border-success/30 px-4 py-2 text-sm text-success">
          Patient discharged on {admission.discharge_date
            ? format(new Date(admission.discharge_date), 'dd MMM yyyy, h:mm a')
            : '—'}
          {' '}· Status: <span className="font-semibold capitalize">{admission.status}</span>
        </div>
      )}

      {/* Summary finalized banner */}
      {isFinalized && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-info/10 border border-info/30 px-4 py-2 text-sm text-info">
          <span>
            Summary finalized on {format(new Date(summary.finalized_at), 'dd MMM yyyy, h:mm a')}
            {summary.finalized_by_name && <> by <span className="font-semibold">{summary.finalized_by_name}</span></>}
            {summary.reopened_at && <> · Amended on {format(new Date(summary.reopened_at), 'dd MMM yyyy')}: {summary.reopen_reason}</>}
          </span>
          {canSign && (
            <Button variant="outline" size="sm" className="gap-1.5 bg-card"
              onClick={() => { setAmendReason(''); setAmendError(''); setAmendOpen(true); }}>
              <Pencil className="w-3.5 h-3.5" /> Amend
            </Button>
          )}
        </div>
      )}
      {!isFinalized && summary?.reopened_at && (
        <div className="rounded-md bg-warning/10 border border-warning/30 px-4 py-2 text-sm text-foreground">
          Reopened for amendment on {format(new Date(summary.reopened_at), 'dd MMM yyyy, h:mm a')}
          {summary.reopened_by_name && <> by <span className="font-semibold">{summary.reopened_by_name}</span></>}
          {' '}— {summary.reopen_reason}. Finalize again when the correction is done.
        </div>
      )}

      {/* ── Section 1: Admission info (read-only) ─────────────────────────── */}
      <div>
        <SectionHeading>Admission</SectionHeading>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Admitted</p>
            <p className="font-medium">
              {admission?.admission_date
                ? format(new Date(admission.admission_date), 'dd MMM yyyy, h:mm a')
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Discharged</p>
            <p className="font-medium">
              {admission?.discharge_date
                ? format(new Date(admission.discharge_date), 'dd MMM yyyy, h:mm a')
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Doctor in charge</p>
            <p className="font-medium">{admission?.admitting_doctor_name || '—'}</p>
          </div>
        </div>
      </div>

      {/* Locked once finalized, or for users who can't save */}
      <fieldset disabled={isFinalized || !canWrite} className="min-w-0 space-y-6">
      {/* ── Section 2: Clinical ───────────────────────────────────────────── */}
      <div>
        <SectionHeading>Clinical details</SectionHeading>
        <div className="space-y-3">
          <LabeledInput
            label="Final diagnosis *"
            value={form.final_diagnosis}
            onChange={set('final_diagnosis')}
            placeholder="Primary diagnosis"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextArea label="Complaints"         value={form.complaints}            onChange={set('complaints')}            rows={2} />
            <TextArea label="Patient history"    value={form.patient_history}       onChange={set('patient_history')}      rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextArea label="Key findings / examination" value={form.key_findings_physical} onChange={set('key_findings_physical')} rows={2} />
            <TextArea label="Investigation summary"               value={form.investigation_summary} onChange={set('investigation_summary')} rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextArea label="Treatment given"     value={form.treatment_given}    onChange={set('treatment_given')}   rows={2} />
            <TextArea label="Surgery / procedure" value={form.procedures_done}    onChange={set('procedures_done')}   rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextArea label="Operative findings"  value={form.operative_findings} onChange={set('operative_findings')} rows={2} />
            <TextArea label="Hospital course"     value={form.hospital_course}    onChange={set('hospital_course')}   rows={2} />
          </div>
          <Select
            label="Condition at discharge"
            value={form.condition_at_discharge}
            onChange={set('condition_at_discharge')}
            options={CONDITIONS.map(c => ({ value: c, label: c[0].toUpperCase() + c.slice(1) }))}
          />
        </div>
      </div>

      {/* ── Section 3: Discharge Medicines ───────────────────────────────── */}
      <div>
        <SectionHeading>Discharge medicines</SectionHeading>
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs">
                {['#', 'Type', 'Medicine', 'Dose', 'When', 'Frequency', 'Duration', 'Notes', ''].map((h, i) => (
                  <th key={i} className="px-2 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.medicines.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No medicines added yet.
                  </td>
                </tr>
              ) : (
                form.medicines.map((m, i) => (
                  <MedicineRow
                    key={i}
                    med={m}
                    index={i}
                    onChange={updated => updateMed(i, updated)}
                    onDelete={() => deleteMed(i)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {!isFinalized && canWrite && (
          <Button variant="outline" size="sm" className="mt-2" onClick={addMedicine}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add medicine
          </Button>
        )}
      </div>

      {/* ── Section 4: Instructions ───────────────────────────────────────── */}
      <div>
        <SectionHeading>Instructions & follow-up</SectionHeading>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextArea label="Discharge advice"    value={form.discharge_advice}    onChange={set('discharge_advice')}    rows={2} />
            <TextArea label="Additional medication (free text)" value={form.additional_medication} onChange={set('additional_medication')} rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextArea label="Others (nutrition, physio, etc.)" value={form.others}           onChange={set('others')}           rows={2} />
            <TextArea label="Tests requested"      value={form.tests_requested}   onChange={set('tests_requested')}   rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LabeledInput label="Follow-up date" type="date" value={form.follow_up_date}  onChange={set('follow_up_date')} />
            <LabeledInput label="Follow-up notes"            value={form.follow_up_notes} onChange={set('follow_up_notes')} placeholder="Optional notes" />
          </div>
          <TextArea label="Acknowledgement (printed as signature line)" value={form.acknowledgement} onChange={set('acknowledgement')} rows={2} />
        </div>
      </div>
      </fieldset>

      {admission && form.follow_up_date && (
        <FollowUpBooking
          date={form.follow_up_date.slice(0, 10)}
          patientId={admission.patient_id}
          clinicId={admission.clinic_id}
          doctorId={admission.admitting_doctor_id}
        />
      )}

      {/* ── Error / success ───────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}
      {savedMsg && (
        <div className="text-sm text-success bg-success/10 border border-success/30 rounded px-3 py-2">
          {savedMsg}
        </div>
      )}

      {/* ── Actions ───────────────────────────────────────────────────────── */}
      {!isFinalized && canWrite && (
        <div className="flex items-center gap-3 pt-2 border-t">
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
          {canSign ? (
            <Button
              onClick={() => { setError(''); setFinalizeOpen(true); }}
              disabled={saving}
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Finalize summary
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">A doctor finalizes the summary.</span>
          )}
        </div>
      )}

      {/* Finalize confirmation dialog */}
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Finalize discharge summary</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This locks the clinical discharge summary. Corrections after this need a reason and are recorded.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalizeOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleFinalize}
              disabled={saving}
              className="bg-success hover:bg-success/90 text-success-foreground"
            >
              {saving ? 'Finalizing…' : 'Finalize'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Amend (reopen) dialog */}
      <Dialog open={amendOpen} onOpenChange={setAmendOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Amend discharge summary</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              The summary unlocks for editing. The finalized version is kept in the record, and you finalize again when done.
            </p>
            <div>
              <label htmlFor="amend-reason" className="block text-[13px] font-semibold text-label mb-1">What needs correcting *</label>
              <Input id="amend-reason" value={amendReason} maxLength={255} autoFocus
                onChange={e => setAmendReason(e.target.value)} placeholder="e.g. Wrong dose for Amoxicillin" className="h-9 text-sm" />
            </div>
            {amendError && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{amendError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleAmend} disabled={saving}>{saving ? 'Reopening…' : 'Reopen for amendment'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print preview */}
      <UnifiedPrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        type="discharge"
        data={buildPrintData()}
      />

    </div>
  );
}

// ─── Follow-up booking ───────────────────────────────────────────────────────

/** The follow-up on the summary as an appointment: shows the booking for that day, or books one (a booking needs a time, so it isn't made silently). */
function FollowUpBooking({ date, patientId, clinicId, doctorId }: { date: string; patientId: string; clinicId: string; doctorId?: string | null }) {
  const { can } = usePermission();
  const [booked, setBooked] = useState<{ time: string } | null | undefined>(undefined);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    getAppointmentsByDateRange(date, date, clinicId)
      .then(list => {
        const apt = (list ?? []).find((a: any) => a.patient_id === patientId && a.status !== 'cancelled' && a.status !== 'no_show');
        setBooked(apt ? { time: apt.time } : null);
      })
      .catch(() => setBooked(null));
  }, [date, patientId, clinicId]);
  useEffect(load, [load]);

  if (booked === undefined || date < format(new Date(), 'yyyy-MM-dd')) return null;
  const day = format(new Date(`${date}T00:00:00`), 'EEE, dd MMM');
  const at = (t: string) => format(new Date(`${date}T${t}`), 'h:mm a');

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
      {booked ? (
        <span className="flex items-center gap-2 text-foreground">
          <CalendarCheck className="w-4 h-4 text-success" /> Follow-up booked · {day}, {at(booked.time)}
        </span>
      ) : (
        <>
          <span className="flex items-center gap-2 text-muted-foreground">
            <CalendarPlus className="w-4 h-4" /> Follow-up on {day} is not booked yet
          </span>
          {can('appointments:write') && (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Book follow-up</Button>
          )}
        </>
      )}
      {open && (
        <AddAppointmentModal
          isOpen
          onClose={() => setOpen(false)}
          patientId={patientId}
          showPatientSelection={false}
          initialDate={date}
          initialDoctorId={doctorId ?? undefined}
          onAdded={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}
